import { type FunctionComponent, useRef } from 'react';

import AbbClose from '@/assets/icons/24x24/abb_close-light_24.svg';
import { Button } from '@/ui-components/button/button';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { Input } from '@/ui-components/input/input';
import { Modal } from '@/ui-components/modal/modal';
import { Paragraph } from '@/ui-components/paragraph/paragraph';
import { Textarea } from '@/ui-components/text-area/text-area';

import { useSupportParams } from './use-support-params';
import { useAuth } from '@power-core/toolkit';

export const SupportModal: FunctionComponent = () => {
	const { auth } = useAuth();
	const { t, data, error, handleChange, handleDrop, handleFileSelect, convertBytesToMB, handleSubmit } = useSupportParams();
	const { subject, description, files, email } = data;
	const fileInputRef = useRef<HTMLInputElement>(null);
	const disabled = !subject || !description || (!auth?.data?.user?.email && !email);

	return (
		<Modal name='Support' contentWidth='max-w-[600px]' dismissible closeButton title={{ text: t('T_REPORT_BUG'), variant: 'h5' }}>
			<Flexbox direction='col' className='max-w-[600px]'>
				{!auth?.data?.user?.email && (
					<Input
						name='email'
						sizing='small'
						value={email}
						onChange={(e) => handleChange({ email: e.target.value })}
						placeholder={t('T_ENTER_EMAIL')}
						label={t('T_EMAIL')}
						containerClassname='mb-5'
						error={error}
					/>
				)}
				<Input
					name='subject'
					sizing='small'
					value={subject}
					onChange={(e) => handleChange({ subject: e.target.value })}
					placeholder={t('T_ENTER_SUBJECT')}
					label={t('T_SUBJECT')}
					containerClassname='mb-5'
				/>
				<Textarea
					id='support'
					labelSize='small'
					value={description}
					onChange={(e) => handleChange({ description: e.target.value })}
					label={t('T_DESCRIPTION')}
					placeholder={t('T_ENTER_MESSAGE')}
					rows={4}
					className='h-[120px] resize-none placeholder:text-sm'
				/>

				<div
					className='mt-5 p-12 border-2 border-dashed border-n400_input_stroker bg-white rounded-lg text-center cursor-pointer hover:border-accent dark:bg-bg_dark dark:text-n700_text_dark_mode'
					onDrop={handleDrop}
					onDragOver={(e) => e.preventDefault()}
					onClick={() => fileInputRef.current?.click()}
				>
					<Paragraph variant='text-sm' className='text-n400_input_stroker'>
						{t('T_DRAG_AND_DROP')}
					</Paragraph>
					<input ref={fileInputRef} id='file-input' type='file' className='hidden' onChange={handleFileSelect} />
				</div>
			</Flexbox>
			{files.length > 0 && (
				<Flexbox direction='col' className='h-24 overflow-y-scroll scrollbar-rounded mt-2 px-2'>
					{files.map((file) => {
						return (
							<Flexbox key={file.lastModified} items='start' justify='between' className='w-full mt-1'>
								<Paragraph variant='text-sm' className='w-5/6'>
									{file.name}
									<span className='text-xs text-n500_light_text'>{` ${convertBytesToMB(file.size)}MB`}</span>
								</Paragraph>

								<AbbClose
									width={18}
									height={18}
									className='cursor-pointer'
									onClick={() => {
										const newFiles = files.filter((f) => f.lastModified !== file.lastModified);
										handleChange({ files: newFiles });
									}}
								/>
							</Flexbox>
						);
					})}
				</Flexbox>
			)}
			<Flexbox justify='end' className='mt-5'>
				<Button variant='primary' sizing='small' disabled={disabled} className='w-28' onClick={handleSubmit}>
					{t('T_SUBMIT')}
				</Button>
			</Flexbox>
		</Modal>
	);
};
